import { Outlet } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Typography } from "@mui/material";
import { MdLockOutline } from "react-icons/md";
import { PermissionsModel } from "../../Redux/Slices/InitialStatePropsModel";
import { isAllowed } from "./permissions";

// Re-exported so the many screens importing these from here keep working. The
// rules themselves live in ./permissions, which has no React imports and can
// therefore be tested.
export {
  isAllowed,
  canView,
  canEdit,
  getFirstPermittedRoute,
  LANDING_PRIORITY,
} from "./permissions";
export type { PermissionAction } from "./permissions";

interface PermissionGuardProps {
  permissionKey: string;
  /**
   * What the page needs, not just which module it belongs to.
   *
   * Every route used to be a view-level gate: the guard took a module name and
   * `canView` passed on view OR edit. So a role with view-only on products
   * could type /add-product and get the blank form. Saving then failed on the
   * API, which is safe but reads as a broken screen rather than as "you may
   * not do this".
   *
   * Pass "edit" on the routes that exist only to create or change something.
   * Defaults to "view" so every existing route behaves exactly as before.
   */
  action?: "view" | "edit";
}

export const NotAuthorized = ({
  action = "view",
}: {
  action?: "view" | "edit";
}) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      minHeight: "60vh",
      gap: 1.5,
      px: 3,
    }}
  >
    <MdLockOutline size={44} style={{ opacity: 0.45 }} />
    <Typography variant="h6" sx={{ fontWeight: 600 }}>
      {action === "edit"
        ? "You have view-only access to this section"
        : "You are not authorized to view this page"}
    </Typography>
    {/*
      Two different situations, and they used to read the same. "You cannot see
      this at all" and "you can see it but not change it" need separate
      sentences, or someone with view-only reads the second as the module
      having been taken away from them.
    */}
    <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 420 }}>
      {action === "edit"
        ? "Your role lets you open this section but not add or change anything here. Ask an administrator if you need to make changes."
        : "Your role does not include access to this section. Ask an administrator if you need it."}
    </Typography>
  </Box>
);

const PermissionGuard = ({
  permissionKey,
  action = "view",
}: PermissionGuardProps) => {
  const permissions: PermissionsModel = useSelector(
    (state: any) => state.AuthSlice.adminAuthUser?.permissions,
  );

  // Show the message rather than redirecting. Redirecting hid the reason and,
  // once every landing candidate could itself be forbidden, risked bouncing
  // between routes.
  return isAllowed(permissions, permissionKey, action) ? (
    <Outlet />
  ) : (
    <NotAuthorized action={action} />
  );
};

export default PermissionGuard;
